import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ArrowRight, Volume2 } from "lucide-react";
import EngineIcon from "./EngineIcon";
import { speak, cancelSpeech } from "@/lib/speak";

// Reusable science exploration engine: predict, test (drop in water), observe.
export default function SinkFloatEngine({ activity, onComplete, autoplay = false }) {
  const cfg = activity.config || {};
  const items = cfg.items || [];
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("predict"); // predict -> result
  const [prediction, setPrediction] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const finished = useRef(false);

  const item = items[idx];
  const done = idx >= items.length;

  const next = () => {
    if (idx + 1 >= items.length) { setIdx(items.length); }
    else { setIdx(idx + 1); setPhase("predict"); setPrediction(null); }
  };

  // Autoplay: read the sink/float question for each new object.
  useEffect(() => {
    if (autoplay && !done && phase === "predict" && item) {
      speak(`Will the ${item.name} sink or float?`, "nova", { speed: 0.9 });
    }
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, phase, autoplay]);

  // Autoplay: after the result shows, narrate it and auto-advance.
  useEffect(() => {
    if (autoplay && phase === "result" && item) {
      speak(`The ${item.name} ${item.floats ? "floats" : "sinks"}!`, "nova", { speed: 0.95 });
      const t = setTimeout(next, 2400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx, autoplay]);

  // Autoplay: auto-finish on the results screen.
  useEffect(() => {
    if (autoplay && done && !finished.current) {
      finished.current = true;
      const t = setTimeout(() => onComplete(Math.round((correctCount / Math.max(1, items.length)) * 100)), 2500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, autoplay]);

  const predict = (floats) => {
    setPrediction(floats);
    const right = floats === item.floats;
    if (right) setCorrectCount((c) => c + 1);
    if (cfg.speak && !autoplay) speak(`${item.name} will ${item.floats ? "float" : "sink"}`);
    setPhase("result");
  };

  if (done) {
    return (
      <div className="text-center" data-testid="sinkfloat-done">
        <p className="font-heading text-2xl font-bold text-[#1F2937]">You tested all {items.length} objects!</p>
        <p className="mt-2 font-body text-[#6B7280]">You predicted {correctCount} of {items.length} correctly.</p>
        <button onClick={() => onComplete(Math.round((correctCount / items.length) * 100))} data-testid="sinkfloat-continue"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95">
          <CheckCircle2 size={18} /> Finish <ArrowRight size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="text-center" data-testid="sinkfloat-engine">
      <p className="font-body text-base sm:text-lg text-[#6B7280]">{activity.prompt}</p>
      <div className="mt-3 font-body text-sm font-semibold uppercase tracking-wide text-[#94A3B8]">Object {idx + 1} of {items.length}</div>

      <div className="mx-auto mt-4 flex items-center justify-center gap-2">
        <h3 className="font-heading text-3xl font-extrabold text-[#1F2937]">{item.name}</h3>
        {cfg.speak && <button onClick={() => speak(`Will the ${item.name} sink or float?`)} className="rounded-full p-2 text-[#0284C7] hover:bg-[#0284C7]/10" data-testid="sinkfloat-speak"><Volume2 size={20} /></button>}
      </div>

      {/* Water tank */}
      <div className="relative mx-auto mt-6 h-56 w-full max-w-sm overflow-hidden rounded-3xl border-2 border-[#0284C7]/30 bg-gradient-to-b from-[#E0F2FE] to-[#7DD3FC]">
        <div className="absolute left-0 right-0 top-1/3 border-t-2 border-dashed border-[#0284C7]/40" />
        <AnimatePresence>
          <motion.div
            key={`${idx}-${phase}`}
            initial={{ y: 8, opacity: 0 }}
            animate={{
              y: phase === "result" ? (item.floats ? 30 : 150) : 8,
              opacity: 1,
            }}
            transition={{ type: "spring", stiffness: 60, damping: 12 }}
            className="absolute left-1/2 -translate-x-1/2 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/90 text-[#1F2937] shadow-lg"
            style={{ top: 0 }}>
            <EngineIcon name={item.icon} size={32} />
          </motion.div>
        </AnimatePresence>
      </div>

      {phase === "predict" ? (
        <div className="mt-6">
          <p className="font-heading text-lg font-bold text-[#1F2937]">Will it sink or float?</p>
          <div className="mt-3 flex justify-center gap-3">
            <button onClick={() => predict(true)} data-testid="predict-float"
              className="rounded-2xl border-2 border-[#0284C7] bg-white px-6 py-3 font-heading font-bold text-[#0284C7] hover:bg-[#0284C7]/10">Float ⬆</button>
            <button onClick={() => predict(false)} data-testid="predict-sink"
              className="rounded-2xl border-2 border-[#E05A47] bg-white px-6 py-3 font-heading font-bold text-[#E05A47] hover:bg-[#E05A47]/10">Sink ⬇</button>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <p className={`font-heading text-xl font-extrabold ${prediction === item.floats ? "text-[#047857]" : "text-[#B45309]"}`} data-testid="sinkfloat-result">
            The {item.name} {item.floats ? "floats!" : "sinks!"} {prediction === item.floats ? "You guessed right! 🎉" : "Good try!"}
          </p>
          <button onClick={next} data-testid="sinkfloat-next"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95">
            {idx + 1 >= items.length ? "See Results" : "Next Object"} <ArrowRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
